import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import MenuList from '@mui/material/MenuList';
import Popper from '@mui/material/Popper';
import { Button } from '@village/ui';
import { LanguageIcon } from 'citymd/icons/language-icon';
import { useCountly } from 'epion-checkin/hooks/use-countly';
import { useIsMobile } from 'epion-checkin/hooks/use-is-mobile';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as Styled from './styles';

const languagesMap = {
    en: {
        mobile: 'EN',
        desktop: 'English',
    },
    es: {
        mobile: 'ES',
        desktop: 'Español',
    },
} as const;

interface Props {
    page: 'welcome' | 'intakeForm';
}

export default function LanguageSelector({ page }: Props) {
    const { i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const { addCountlyEvent } = useCountly();
    const anchorRef = useRef<null | HTMLButtonElement>(null);
    const device = useIsMobile() ? 'mobile' : 'desktop';

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: MouseEvent | TouchEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.currentTarget as HTMLElement)) {
            return;
        }

        setOpen(false);
    };

    const handleLanguageChange = (language: string) => {
        i18n.changeLanguage(language);
        setOpen(false);

        if (language === 'es') {
            addCountlyEvent({ key: page === 'intakeForm' ? 'viewpatientintake.clickspanish' : 'viewwelcome.clickspanish' });
        }
    };

    // return focus to the button when we transitioned from !open -> open
    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current?.focus();
        }

        prevOpen.current = open;
    }, [open]);

    return (
        <div>
            <Button
                ref={anchorRef}
                variant="link"
                aria-haspopup="true"
                onClick={handleToggle}
                startIcon={device === 'desktop' ? <LanguageIcon /> : undefined}
                aria-controls={open ? 'languages-list' : undefined}
            >
                {i18n.language === 'es' ? languagesMap['es'][device] : languagesMap['en'][device]}
            </Button>
            <Popper open={open} anchorEl={anchorRef.current} role={undefined} transition disablePortal placement="bottom-end">
                {({ TransitionProps }) => (
                    <Grow {...TransitionProps} style={{ transformOrigin: 'left top' }}>
                        <Styled.LanguagePaper elevation={8} variant="elevation">
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList autoFocusItem={open} id="languages-list" variant="selectedMenu">
                                    <Styled.LanguageMenuItem onClick={() => handleLanguageChange('en')}>
                                        <Styled.LanguageRadio checked={i18n.language === 'en'} label="English" />
                                    </Styled.LanguageMenuItem>
                                    <Styled.LanguageMenuItem onClick={() => handleLanguageChange('es')}>
                                        <Styled.LanguageRadio checked={i18n.language === 'es'} label="Español" />
                                    </Styled.LanguageMenuItem>
                                </MenuList>
                            </ClickAwayListener>
                        </Styled.LanguagePaper>
                    </Grow>
                )}
            </Popper>
        </div>
    );
}
